import { Component, computed, inject, Pipe, PipeTransform } from '@angular/core';
import { MedicalServiceLogsPageStore } from './medical-service-logs-page.store';
import { CardModule } from '@openng/optimus-ui/card';
import { TranslatePipe } from '@ngx-translate/core';
import { routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { TableColumn, TableColumnType } from '../../../../modules/shared/components/kit/table/table.types';

import {
    AuditLogTreeV2Component,
} from '../../../../modules/shared/components/audit-log-tree-v2/audit-log-tree-v2.component';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { LocalDatePipe } from '../../../../modules/shared/pipes/local-date.pipe';

@Pipe({
    name: 'getEntityDataSummary',
})

export class GetEntityDataSummaryPipe implements PipeTransform {
    transform(value: any) {
        let data = JSON.parse(value.data || '{}');
        if (!Object.keys(data).length) {
            data = JSON.parse(value.oldData || '{}');
        }

        switch (value.entityName) {
            case 'AnamnesisModel':
            case 'CarePlanModel':
            case 'EPrescritpionModel':
            case 'FolderDataShareModel':
            case 'FolderFormModel':
            case 'FolderMedicalReportModel':
            case 'FolderMedicationModel':
            case 'FolderNoteModel':
            case 'FolderPalliativeCareModel':
            case 'FolderPaymentDetailsModel':
            case 'FolderRiskStatusModel':
            case 'FolderStatusHistoryModel':
            case 'FolderWorkgroupModel':
            case 'PersonContactModel':
            case 'VoucherModel':
            case 'WorkModel':
                return formatPerson(data.folder?.person) || '';
            case 'BillingModel':
                return data.insuranceCompany?.code || '';
            case 'FolderModel':
                return formatPerson(data.person) || '';
            case 'InvoiceModel':
                return `${data.insuranceCompany?.code},
                    ${translate.instant('enum.insuranceType.' + data.insuranceType)} -
                    ${data.year}/${data.month.toString().padStart(2, '0')}`;
            case 'PersonModel':
                return formatPerson(data);
            case 'ServiceNursingCareDiagnosisCodeModel':
                return `${data.code || ''} - ${data.description || ''}`;
            case 'ServiceNursingCareDiagnosisGroupModel':
            case 'ServiceDoctorRecordTypeModel':
            case 'ServiceGroupModel':
            case 'ServiceLocationModel':
            case 'ServiceOperationalCapacityModel':
            case 'ServicePatternModel':
            case 'ServiceSpecialistRecordContactTypeModel':
            case 'ServiceSpecialistRecordWorkTypeModel':
            case 'ServiceWorkgroupModel':
            case 'WorkplaceModel':
                return data.name || '';
            case 'ServiceDataShareModel':
                return `${translate.instant('entities.history.data.sharingWith')}
                    ${this.getServiceCurrentName(data.serviceToShare?.basicInfos || [])}`;
            case 'ServiceMedicalProcedureModel':
                return data.code || '';
            case 'ServiceModel':
                return this.getServiceCurrentName(data.basicInfos || []);
            case 'ServiceOperationalReportModel':
                return `${data.type || '-'}, ${data.shift || '-'}, ${data.form || '-'}`;
            case 'ServicePaymentModel':
                return `${data.insuranceCompany?.code}, ${translate.instant('enum.insuranceType.' + data.insuranceType)}`;
            case 'ServiceSettingsModel':
                return data.key || '';
            default:
                return '';
        }
    }

    getServiceCurrentName(basicInfos: any[]) {
        const currentBasicInfo = basicInfos
            .filter((it) => new Date() > new Date(it.validFrom))
            .sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime());
        return currentBasicInfo[0]?.name || '';
    }
}

@Component({
    selector: 'app-medical-service-logs-page',
    templateUrl: 'medical-service-logs-page.component.html',
    styleUrl: 'medical-service-logs-page.component.scss',
    providers: [MedicalServiceLogsPageStore],
    imports: [
        CardModule,
        TranslatePipe,
        GetEntityDataSummaryPipe,
        AuditLogTreeV2Component,
        CardTableComponent,
        PrimeTemplate,
        LocalDatePipe,
    ],
})
export class MedicalServiceLogsPageComponent {

    public readonly store = inject(MedicalServiceLogsPageStore);

    protected filters = computed(() => [
        [{ column: 'serviceId', operator: 'EQ', value: [routeParams.serviceId()] }],
    ]);

    protected columns: TableColumn[] = [
        {
            name: 'date',
            dataKey: 'timestamp',
            type: TableColumnType.Date,
        },
        {
            name: 'time',
            dataKey: 'timestamp',
            type: TableColumnType.Time,
        },
        {
            name: 'action',
            data: (data) => translate.instant('entities.history.actions.' + data),
            filterable: {
                type: 'select',
                selectOptions: () => this.actions.map((action) => ({
                    name: translate.instant('entities.history.actions.' + action),
                    value: action,
                })),
            },
        },
        {
            name: 'entityName',
            data: (data) => translate.instant('entities.' + data),
            filterable: {
                type: 'select',
                selectOptions: () => this.entites.map((entity) => ({
                    name: translate.instant('entities.' + entity),
                    value: entity,
                })).sort((a, b) => a.name.localeCompare(b.name)),
            },
        },
        {
            name: 'data',
            filterable: { type: 'text' },
        },
        {
            name: 'authorName',
            filterable: { type: 'text' },
        },
    ];

    private actions = [
        'read',
        'create',
        'update',
        'delete',
    ];

    private entites = [
        'AnamnesisModel',
        'BillingModel',
        'CarePlanModel',
        'FolderModel',
        'FolderDataShareModel',
        'FolderFormModel',
        'FolderMedicalReportModel',
        'FolderMedicationModel',
        'FolderNoteModel',
        'FolderPalliativeCareModel',
        'FolderPaymentDetailsModel',
        'FolderRiskStatusModel',
        'FolderWorkgroupModel',
        'EPrescriptionModel',
        'InvoiceModel',
        'PersonContactModel',
        'PersonModel',
        'ServiceDataShareModel',
        'ServiceDoctorRecordTypeModel',
        'ServiceGroupModel',
        'ServiceLocationModel',
        'ServiceMedicalProcedureModel',
        'ServiceModel',
        'ServiceNursingCareDiagnosisCodeModel',
        'ServiceNursingCareDiagnosisGroupModel',
        'ServiceOperationalCapacityModel',
        'ServiceOperationalReportModel',
        'ServicePatternModel',
        'ServicePaymentModel',
        'ServiceSettingsModel',
        'ServiceSpecialistRecordContactTypeModel',
        'ServiceSpecialistRecordWorkTypeModel',
        'ServiceWorkgroupModel',
        'WorkModel',
        'WorkplaceModel',
    ];

}
